import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { useMutation, ApolloError } from '@apollo/client'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { useLanguage } from '@/context/LanguageContext'
import { CREATE_PAGE } from '../pageQueries'
import { buildPageProfiles } from '../pageProfiles'
import { generateSlug, sanitizeSlug } from '../slugUtils'

const quickCreateSchema = z.object({
  code: z
    .string()
    .min(1, 'Code is required')
    .regex(/^[a-z0-9-]+$/, 'Lowercase alphanumeric with hyphens only'),
  title: z.string().min(1, 'Title is required'),
  slug: z.string().min(1, 'Slug is required'),
})

type QuickCreateValues = z.infer<typeof quickCreateSchema>

interface QuickCreatePageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function QuickCreatePageDialog({
  open,
  onOpenChange,
}: QuickCreatePageDialogProps) {
  const navigate = useNavigate()
  const { allLanguages, languageId, languagesLoading } = useLanguage()

  // `LanguageProvider` remounts on every route change and starts with
  // `allLanguages = []` until GET_SITE_ENABLED_LANGUAGES resolves (see
  // LanguageContext.tsx). Submitting before it resolves would map an empty
  // `allLanguages` into `page_profiles: []` (via `buildPageProfiles` below),
  // creating a page with ZERO PageProfile rows — Title/Slug silently lost.
  // Gate on the explicit loading flag (not just `allLanguages.length === 0`)
  // so a shop genuinely configured with no enabled languages doesn't get
  // stuck showing "Loading languages..." forever.
  const languagesNotReady = languagesLoading || allLanguages.length === 0

  const form = useForm<QuickCreateValues>({
    resolver: zodResolver(quickCreateSchema),
    mode: 'onSubmit',
    defaultValues: { code: '', title: '', slug: '' },
  })

  // Reset when the dialog opens.
  useEffect(() => {
    if (open) {
      form.reset({ code: '', title: '', slug: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleCreatePageError = (error: ApolloError) => {
    const messages: string[] = [
      ...(error?.graphQLErrors?.map(e => e.message) ?? []),
      error?.message,
    ].filter(Boolean)

    const isDuplicateCodeError = messages.some(message => {
      const lower = message.toLowerCase()
      return (
        (lower.includes('code') && lower.includes('already exists')) ||
        (lower.includes('unique constraint failed') && lower.includes('code'))
      )
    })

    if (isDuplicateCodeError) {
      form.setError('code', {
        type: 'server',
        message: 'This code is already in use',
      })
      return // Don't show toast for this specific error
    }

    // Surface the actual server message when there is one — e.g. the
    // backend's "A page must be created with at least one language profile"
    // guard (rejects an empty `page_profiles`, which is exactly what this
    // dialog would have sent pre-fix while `allLanguages` was still loading;
    // see `languagesNotReady` above). Fall back to the generic message only
    // when no usable message came back (network error, etc.).
    toast.error(messages[0] || 'Failed to create page')
  }

  const [createPage, { loading }] = useMutation(CREATE_PAGE, {
    onError: error => {
      handleCreatePageError(error)
    },
    onCompleted: response => {
      const newId = response?.createNewPage?.id
      toast.success('Page created successfully')
      onOpenChange(false)
      if (newId) navigate(`/pages/${newId}`)
    },
    refetchQueries: ['GET_PAGES', 'GET_PAGES_TREE'],
  })

  // Auto-generate slug from title while slug is empty / untouched.
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value
    const currentSlug = form.getValues('slug')
    const currentTitle = form.getValues('title')

    form.setValue('title', newTitle, { shouldValidate: false })
    if (!currentSlug || currentSlug === generateSlug(currentTitle)) {
      form.setValue('slug', generateSlug(newTitle), { shouldValidate: false })
    }
  }

  const onSubmit = (values: QuickCreateValues) => {
    // Belt-and-suspenders: the submit button is disabled while languages
    // aren't ready, but guard here too in case of a stray Enter-key submit.
    if (languagesNotReady) {
      toast.error('Still loading languages — please wait a moment and try again.')
      return
    }

    const lang = allLanguages[languageId] ?? allLanguages[0] ?? 'en'
    const profiles = allLanguages.map(l => ({
      language: l,
      title: l === lang ? values.title : '',
      slug: l === lang ? values.slug : '',
      subtitle: '',
      meta_description: '',
    }))

    createPage({
      variables: {
        code: values.code,
        page_profiles: buildPageProfiles(profiles),
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Page</DialogTitle>
          <DialogDescription>
            Create a page, then edit its content and settings.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <InputFieldContainer
              label="Code"
              description="Unique identifier (lowercase, hyphens only)"
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <Input
                      placeholder="e.g., about-us"
                      type="text"
                      {...field}
                      onChange={e => {
                        field.onChange(e)
                        if (form.formState.errors.code?.type === 'server') {
                          form.clearErrors('code')
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <InputFieldContainer
              label="Title"
              description="Page title for display and SEO"
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <Input
                      placeholder="e.g., About Us"
                      type="text"
                      {...field}
                      onChange={handleTitleChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <InputFieldContainer
              label="Slug"
              description="URL path (auto-generated from title)"
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormControl>
                    <Input
                      placeholder="e.g., about-us"
                      type="text"
                      {...field}
                      onChange={e =>
                        field.onChange(sanitizeSlug(e.target.value))
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading || languagesNotReady}>
                {loading
                  ? 'Creating...'
                  : languagesLoading
                    ? 'Loading languages...'
                    : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
